/**
 * The latest report in full, one tab at a time: plugins, themes, the file
 * checks, backups and the database (item J, `BackupTab`, shown when the report
 * or the package has anything to say about them), and the short history of
 * reports.
 *
 * The plugin list leads with the ones that have an update waiting, because
 * that is the list somebody opens this card to work through. The file tab
 * says how complete each check was: a check that ran out of time or hit its
 * list ceiling says so, and never reads as "nothing found".
 */
import { useState } from "react";
import { cn } from "@wa/ui";
import { showBackupBlock } from "../../../../lib/monitoringBackup";
import {
  orderPlugins,
  type ReporterExpectView,
  type ReporterHistoryRow,
  type ReporterReportView,
} from "../../../../lib/monitoringReporter";
import { PillTabs, StatusPill, fmtBytes, fmtDateTime, fmtNum } from "../../../ui";
import type { T } from "../words";
import { BackupTab } from "./BackupTab";

type Tab = "plugins" | "themes" | "files" | "backup" | "history";

function UpdateCell({ update, t }: { update: string | null; t: T }) {
  return update ? (
    <StatusPill tone="wt">{update}</StatusPill>
  ) : (
    <span className="crm-mut">{t("monitoring.reporter.upToDate")}</span>
  );
}

function PluginsTable({ report, t }: { report: ReporterReportView; t: T }) {
  if (report.plugins.length === 0 && report.muPlugins.length === 0) {
    return <p className="crm-mut text-[13px]">{t("monitoring.reporter.noPlugins")}</p>;
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="crm-table">
          <thead>
            <tr>
              <th scope="col">{t("monitoring.reporter.col.name")}</th>
              <th scope="col">{t("monitoring.reporter.col.version")}</th>
              <th scope="col">{t("monitoring.reporter.col.update")}</th>
              <th scope="col">{t("monitoring.reporter.col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {orderPlugins(report.plugins).map((p) => (
              <tr key={p.file}>
                <td>
                  <span className="block font-semibold">{p.name || p.slug}</span>
                  <span className="crm-mut block text-[11.5px]">{p.file}</span>
                </td>
                <td className="crm-tnum">{p.version || "-"}</td>
                <td>
                  <UpdateCell update={p.update} t={t} />
                </td>
                <td className={cn(!p.active && "crm-mut")}>
                  {t(p.active ? "monitoring.reporter.active" : "monitoring.reporter.inactive")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report.muPlugins.length > 0 ? (
        <div className="mt-4">
          <p className="crm-prow-n mb-1.5">{t("monitoring.reporter.muTitle")}</p>
          <ul className="crm-sub space-y-0.5 text-[13px]">
            {report.muPlugins.map((m) => (
              <li key={m.file}>
                <span className="font-mono text-[12px]">{m.file}</span>
                {m.name ? ` · ${m.name}` : ""}
                {m.version ? ` ${m.version}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

function ThemesTable({ report, t }: { report: ReporterReportView; t: T }) {
  if (report.themes.length === 0) return <p className="crm-mut text-[13px]">{t("monitoring.reporter.noThemes")}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="crm-table">
        <thead>
          <tr>
            <th scope="col">{t("monitoring.reporter.col.name")}</th>
            <th scope="col">{t("monitoring.reporter.col.version")}</th>
            <th scope="col">{t("monitoring.reporter.col.update")}</th>
            <th scope="col">{t("monitoring.reporter.col.status")}</th>
          </tr>
        </thead>
        <tbody>
          {report.themes.map((th) => (
            <tr key={th.slug}>
              <td>
                <span className="block font-semibold">{th.name || th.slug}</span>
                <span className="crm-mut block text-[11.5px]">
                  {th.parent ? t("monitoring.reporter.childOf", { slug: th.slug, parent: th.parent }) : th.slug}
                </span>
              </td>
              <td className="crm-tnum">{th.version || "-"}</td>
              <td>
                <UpdateCell update={th.update} t={t} />
              </td>
              <td className={cn(!th.active && "crm-mut")}>
                {t(th.active ? "monitoring.reporter.active" : "monitoring.reporter.inactive")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PathList({ title, paths, t }: { title: string; paths: string[]; t: T }) {
  return (
    <div>
      <p className="crm-prow-n mb-1">
        {title} <span className="crm-mut crm-tnum">({paths.length})</span>
      </p>
      {paths.length === 0 ? (
        <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.files.none")}</p>
      ) : (
        <ul className="space-y-0.5 font-mono text-[12px]">
          {paths.map((p) => (
            <li key={p} className="break-all">
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilesPanel({ report, t }: { report: ReporterReportView; t: T }) {
  const i = report.integrity;
  const u = report.uploads;
  const e = report.exposure;
  const flags: string[] = [];
  if (e.wpDebug) flags.push(t("monitoring.reporter.files.wpDebugOn"));
  if (e.wpDebug && e.debugDisplay) flags.push(t("monitoring.reporter.files.debugDisplayOn"));
  if (e.displayErrors) flags.push(t("monitoring.reporter.files.displayErrorsOn"));
  if (e.debugLogBytes !== null) flags.push(t("monitoring.reporter.files.debugLogFile", { size: fmtBytes(e.debugLogBytes) }));
  if (e.fileEdit) flags.push(t("monitoring.reporter.files.fileEditOn"));
  for (const name of e.exposedFiles) flags.push(t("monitoring.reporter.files.exposed", { name }));

  return (
    <div className="space-y-6 text-[13px]">
      <section>
        <p className="font-semibold">{t("monitoring.reporter.files.integrityTitle")}</p>
        <p className="crm-sub mb-3">
          {t(`monitoring.reporter.files.status.${i.status}`)}{" "}
          {t("monitoring.reporter.files.checked", { n: i.checked })}
          {i.truncated ? ` ${t("monitoring.reporter.files.truncated")}` : ""}
        </p>
        {/* Stacked, not side by side: a core path is 40 to 60 characters of
            monospace and breaks mid-name in a third of the card. */}
        <div className="space-y-3">
          <PathList title={t("monitoring.reporter.files.modified")} paths={i.modified} t={t} />
          <PathList title={t("monitoring.reporter.files.unexpected")} paths={i.unexpected} t={t} />
          <PathList title={t("monitoring.reporter.files.missing")} paths={i.missing} t={t} />
        </div>
      </section>

      <section>
        <p className="font-semibold">{t("monitoring.reporter.files.uploadsTitle")}</p>
        <p className="crm-sub mb-3">
          {t(`monitoring.reporter.files.status.${u.status}`)}{" "}
          {t("monitoring.reporter.files.scanned", { n: u.scanned })}
          {u.truncated ? ` ${t("monitoring.reporter.files.truncated")}` : ""}
        </p>
        {u.files.length === 0 ? (
          <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.files.none")}</p>
        ) : (
          <ul className="space-y-1">
            {/* Files with code first: they are the finding. Guard files follow, muted. */}
            {[...u.files].sort((a, b) => Number(b.code) - Number(a.code)).map((f) => (
              <li key={f.path} className={cn("flex flex-wrap items-baseline gap-x-3", !f.code && "crm-mut")}>
                <span className={cn("break-all font-mono text-[12px]", f.code && "text-[var(--color-destructive)]")}>{f.path}</span>
                <span className="crm-mut crm-tnum text-[11.5px]">
                  {fmtBytes(f.size)} · {fmtDateTime(f.modifiedAt)}
                  {f.code ? "" : ` · ${t("monitoring.reporter.files.guard")}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <p className="font-semibold">{t("monitoring.reporter.files.exposureTitle")}</p>
        {flags.length === 0 ? (
          <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.files.exposureNone")}</p>
        ) : (
          <ul className="crm-sub list-disc space-y-0.5 pl-5">
            {flags.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function HistoryTable({ history, t }: { history: ReporterHistoryRow[]; t: T }) {
  return (
    <div className="overflow-x-auto">
      <table className="crm-table">
        <thead>
          <tr>
            <th scope="col">{t("monitoring.reporter.col.received")}</th>
            <th scope="col">{t("monitoring.reporter.col.reason")}</th>
            <th scope="col">WordPress / PHP</th>
            <th scope="col" className="text-right">
              {t("monitoring.reporter.col.updates")}
            </th>
            <th scope="col" className="text-right">
              {t("monitoring.reporter.col.fileIssues")}
            </th>
            <th scope="col" className="text-right">
              {t("monitoring.reporter.col.changes")}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map((h) => (
            <tr key={h.id}>
              <td className="crm-tnum">{fmtDateTime(h.receivedAt)}</td>
              <td>{t(`monitoring.reporter.reason.${h.reason}`)}</td>
              <td className="crm-tnum">
                {h.coreVersion} / {h.phpVersion}
              </td>
              <td className="crm-tnum text-right">{fmtNum(h.updates)}</td>
              <td className={cn("crm-tnum text-right", h.integrityIssues + h.uploadsPhp > 0 && "text-[var(--color-destructive)]")}>
                {fmtNum(h.integrityIssues + h.uploadsPhp)}
              </td>
              <td className="crm-tnum text-right">{h.changes === null ? "-" : fmtNum(h.changes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReportTabs({
  report,
  history,
  expect,
  t,
}: {
  report: ReporterReportView;
  history: ReporterHistoryRow[];
  expect: ReporterExpectView;
  t: T;
}) {
  const [picked, setTab] = useState<Tab>("plugins");
  const hasBackup = showBackupBlock(report, expect);
  // A newer report without the backup tab falls back to the first tab rather than an empty panel.
  const tab: Tab = picked === "backup" && !hasBackup ? "plugins" : picked;
  const fileIssues =
    report.integrity.modified.length +
    report.integrity.unexpected.length +
    report.integrity.missing.length +
    report.uploads.files.filter((f) => f.code).length;
  const tabs = [
    { value: "plugins" as const, label: t("monitoring.reporter.tab.plugins"), count: report.plugins.length },
    { value: "themes" as const, label: t("monitoring.reporter.tab.themes"), count: report.themes.length },
    { value: "files" as const, label: t("monitoring.reporter.tab.files"), count: fileIssues || undefined },
    ...(hasBackup ? [{ value: "backup" as const, label: t("monitoring.reporter.tab.backup") }] : []),
    { value: "history" as const, label: t("monitoring.reporter.tab.history"), count: history.length },
  ];
  return (
    <div className="px-[26px] pb-6 pt-5">
      <PillTabs tabs={tabs} value={tab} onChange={setTab} small rail className="mb-4" />
      {tab === "plugins" ? <PluginsTable report={report} t={t} /> : null}
      {tab === "themes" ? <ThemesTable report={report} t={t} /> : null}
      {tab === "files" ? <FilesPanel report={report} t={t} /> : null}
      {tab === "backup" ? <BackupTab report={report} history={history} expect={expect} t={t} /> : null}
      {tab === "history" ? <HistoryTable history={history} t={t} /> : null}
    </div>
  );
}
